<template>
  <div class="tax-invoice-request-display">
    <div class="card card-default mb-4">
      <h4 
        class="card-header" 
        v-html="title" />
      <div class="card-body">
        <p class="text-muted">
          {{ incidentReport.StaffMember.fullName }} - <i class="far fa-clock fa-fw" /> {{ incidentReport.dateOfCall | date_time_format }}
          <span class="float-right" v-if="$user.administrator"><i class="fas fa-stopwatch fa-fw" /> Time spent: {{ timeSpent }}</span>
        </p>
        <template v-if="!$route.meta.client && incidentReport.TaxInvoiceRequest">
          <p>
            <strong>
              Tax Invoice Request
            </strong>
            <br>
            <download-input
              :options="{ label: incidentReport.TaxInvoiceRequest.referenceNumber, permalink: 'downloadFile'}"
              :value="{ objectKey: incidentReport.TaxInvoiceRequest.InvoiceRequestPdf.objectKey }" />
          </p>
          <p v-if="incidentReport.TaxInvoiceRequest.UploadedInvoice">
            <strong>
              Uploaded Invoice
            </strong>
            <br>
            <download-input
              :options="{ label: incidentReport.TaxInvoiceRequest.UploadedInvoice.fileName, permalink: 'downloadFile'}"
              :value="{ objectKey: incidentReport.TaxInvoiceRequest.UploadedInvoice.objectKey }" />
          </p>
        </template>
        <template>
          <p v-if="this.$route.meta.client && !incidentReport.clientInvoiced">
            <strong>
              Invoice Pending...
            </strong>
          </p>
        </template>
        <template>
          <p v-if="!$route.meta.client && incidentReport.TaxInvoiceRequest && incidentReport.TaxInvoiceRequest.UploadedInvoice"
            class="checkbox-inline">
            <input type="checkbox" @change="oCManagerInvoice()" v-model="incidentReport.clientInvoiced" />
            <strong> Send invoice to OC Manager</strong>
          </p>
        </template>
        <template v-if="incidentReport.SentEmails.length && !$route.meta.client">
          <sent-email
            v-for="email in incidentReport.SentEmails"
            :key="email.uuid"
            :email="email">
          </sent-email>
        </template>
      </div>
      <div class="card-footer">
        <router-link
          :to="{ path: 'edit/' + incidentReport.uuid, append: true }"
          v-if="!$route.meta.client"
          class="my-1 btn btn-black">
          <i class="far fa-fw fa-edit" /> Edit
        </router-link>
        <router-link
          v-if="!$route.meta.client"
          target="_blank"
          :to="{ path: '/api/public/pdf/' + incidentReport.uuid + '?m=IncidentReport' }"
          class="my-1 btn btn-secondary"
          :class="{ 'disabled': !incidentReport.ReportPdfUuid }">
          <i class="fa fa-fw fa-search" /> Preview
        </router-link>
        <download-input
          :disabled="!incidentReport.ReportPdfUuid"
          :options="{ button: true, label: 'Download Report', permalink: 'downloadFile'}"
          :value="{ objectKey: incidentReport.ReportPdf ? incidentReport.ReportPdf.objectKey : '' }" />
        <download-input
          v-if="this.$route.meta.client && incidentReport.clientInvoiced"
          :options="{ button: true, label: 'Download Invoice', permalink: 'downloadFile'}"
          :value="{ objectKey: incidentReport.TaxInvoiceRequest.UploadedInvoice.objectKey }" />
        <button
          v-if="!$route.meta.client" 
          class="my-1 btn btn-primary" 
          @click="generatePdf()">
          <i class="fa fa-fw fa-redo-alt" /> Generate PDF
        </button>
        <button 
          v-if="!$route.meta.client"
          class="my-1 btn btn-info"
          @click="upload()">
          <i class="fa fa-fw fa-upload" /> Upload
        </button>
        <button 
          v-if="!$route.meta.client"
          class="my-1 btn btn-success" 
          @click="send()" 
          :disabled="!incidentReport.complete">
          <i class="far fa-fw fa-paper-plane" /> Send
        </button>
        <button 
          v-if="!$route.meta.client"
          class="my-1 btn btn-lime" 
          @click="duplicate()">
          <i class="far fa-fw fa-copy" /> Duplicate
        </button>
        <router-link 
          v-if="!$route.meta.client"
          :to="'/tax-invoice-requests/new/' + incidentReport.uuid + '?t=IncidentReport'"
          class="my-1 btn btn-warning"
          :class="{ 'disabled': !generateInvoiceButton }">
          <i class="fa fa-fw fa-dollar-sign" /> Invoice Request
        </router-link>
        <button 
          v-if="!$route.meta.client"
          @click="deleteIncidentReport()" 
          class="my-1 btn btn-danger">
          <i class="fa fa-fw fa-trash" /> Delete
        </button>
      </div>
    </div>
  </div>
</template>
<script>
  import DownloadInput from '@/components/inputs/DownloadInput'
  import {
    INVOICE_STATUS_OCMANAGER,
    DELETE_REPORT,
    DUPLICATE_INCIDENT_REPORT,
    GENERATE_INCIDENT_PDF,
    SEND_INCIDENT_REPORT,
    UPLOAD_INCIDENT_PDF
  } from '@/vuex/types'
  import SentEmail from '@/components/displays/SentEmail'
  export default {
    name: 'IncidentReportDisplay',
    props: {
      object: {
        type: Object,
        default () {
          return {}
        }
      }
    },
    components: {
      SentEmail,
      DownloadInput
    },
    computed: {
      timeSpent () {
        let minutes = this.incidentReport.time / 60.0
        if (minutes > 120) {
          let hours = minutes / 60
          return hours.toFixed(2) + ' hours'
        } else {
          return minutes.toFixed(2) + ' min'
        }
      },
      fileName () {
        if (this.incidentReport.ReportPdf) {
          return this.incidentReport.ReportPdf.fileName.slice(0, -4)
        } else {
          return this.incidentReport.propertyAddress
        }
      },
      generateInvoiceButton () {
        return this.incidentReport.complete && !this.incidentReport.TaxInvoiceRequestUuid
      },
      title () {
        if (this.incidentReport.propertyAddress) {
          return '<i class="fa fa-fw fa-map-marker-alt"></i> ' + this.incidentReport.propertyAddress
        } else {
          return '<i class="fa fa-fw fa-certificate"></i> New Report'
        }
      },
      incidentReport () {
        return {
          StaffMember: {},
          ...this.object
        }
      }
    },
    methods: {
      async oCManagerInvoice () {
        await this.$store.dispatch(INVOICE_STATUS_OCMANAGER, {
          incidentReportUuid: this.incidentReport.uuid,
          oCManagerInvoiced: this.incidentReport.clientInvoiced
        })
      },
      async deleteIncidentReport () {
        await this.$store.dispatch(DELETE_REPORT, {
          reportUuid: this.incidentReport.uuid,
          reportType: 'IncidentReport'
        })
      },
      async generatePdf () {
        await this.$store.dispatch(GENERATE_INCIDENT_PDF, this.incidentReport)
      },
      async upload () {
        await this.$store.dispatch(UPLOAD_INCIDENT_PDF, this.incidentReport.uuid)
      },
      async duplicate () {
        await this.$store.dispatch(DUPLICATE_INCIDENT_REPORT, this.incidentReport.uuid)
      },
      async send () {
        await this.$store.dispatch(SEND_INCIDENT_REPORT, this.incidentReport.uuid)
      }
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/cards/IncidentReport.vue